import { type CityNotice } from './cityNotice';
import { LiveAnnouncer } from './LiveAnnouncer';
import { LocalityOfferBox } from './LocalityOfferBox';
import classes from './LocalityFields.module.css';

// @ai-generated 2026-09-14T00:00:00+00:00 by matthias.gessinger@synqony.com using Claude Opus 5

export interface CityNoticeAreaProps {
    notice: CityNotice | undefined;
    /**
     * Fills the line while the automation has nothing to say — where the city is going to come
     * from, for somebody who has not seen it happen yet. It is standing text, so it is shown but
     * never announced: a screen reader would otherwise read it as news on every render.
     */
    hint?: string;
    onAccept: (locality: string) => void;
    onKeep: () => void;
}

/**
 * Everything the automation says below the city field: a line of state, or the localities of the
 * postal code to choose from. Never both — where there is something to pick, picking it is the
 * whole message.
 */
export function CityNoticeArea({ notice, hint, onAccept, onKeep }: CityNoticeAreaProps) {
    if (notice?.kind === 'offer') {
        return (
            <>
                <LocalityOfferBox offer={notice.offer} statement={notice.text} onAccept={onAccept} onKeep={onKeep} />
                {/* A contested offer carries role="alert" and announces itself; announcing it again
                    here would tell a screen reader the same thing twice. */}
                <LiveAnnouncer message={notice.offer.contested === undefined ? notice.text : ''} />
            </>
        );
    }

    // Only `success` reads differently; every other tone is the plain line, which is the base style.
    const toneClass = notice?.tone === 'success' ? `${classes.notice} ${classes.noticeSuccess}` : classes.notice;

    return (
        <>
            {/* Rendered even with nothing to say: the line reserves its space (see CSS), otherwise
                every state change would shift the form below it up and down. */}
            <span className={toneClass}>{notice?.text ?? hint ?? ''}</span>
            {/* Progress is not announced: it is a side effect of typing and says nothing the user
                can act on. */}
            <LiveAnnouncer message={notice !== undefined && notice.tone !== 'progress' ? notice.text : ''} />
        </>
    );
}
